<template>
    <!-- This <template> must have EXACTLY one <td> per grid header
         (16 cells = 1 ID col + 15 tableHeaders entries). The order
         below mirrors `tableHeaders` in InventoryItems.vue. -->

    <!-- 1. ID column (auto-prepended TablePage header) -->
    <td class="border-l border-b border-gray-300 px-1 py-1 text-xs align-middle text-center">
        <form @submit.prevent="addInventoryItem" :id="'tableForm' + index" action=""></form>
        <base-pill class="bg-amber-300 border-none text-dark-primary p-0">Unsaved</base-pill>
    </td>

    <!-- 2. Category icon column -->
    <td class="border-b border-r border-gray-300 px-0.5 py-1 text-xs align-middle">
        <select :form="'tableForm' + index" v-model="inventoryItemData.category"
            class="p-1 h-full w-full rounded border cursor-pointer text-xs"
            :required="!inventoryItemData.brand && !inventoryItemData.model"
            :title="'Category'"
        >
            <option :value="null">—</option>
            <option v-for="option in inventoryCategoryOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
    </td>

    <!-- 3. Device Name -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="text" v-model="inventoryItemData.asset_name"
            placeholder="Device name" class="p-1 h-full w-full font-mono" />
    </td>

    <!-- 4. Old Name -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="text" v-model="inventoryItemData.old_device_name"
            placeholder="Old name" class="p-1 h-full w-full font-mono" />
    </td>

    <!-- 5. Serial # -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="text" v-model="inventoryItemData.serial_number"
            placeholder="Serial #" class="p-1 h-full w-full font-mono" />
    </td>

    <!-- 6. A1 -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle text-center">
        <input :form="'tableForm' + index" type="checkbox" v-model="inventoryItemData.action1_installed"
            class="w-6 h-6 cursor-pointer accent-gray-600" :title="'Action1 installed?'" />
    </td>

    <!-- 7. Specs -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle text-center">
        <input :form="'tableForm' + index" type="checkbox" v-model="inventoryItemData.action1_specs_captured"
            class="w-6 h-6 cursor-pointer accent-gray-600" :title="'Specs captured via Action1?'" />
    </td>

    <!-- 8. Next Steps -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="text" v-model="inventoryItemData.next_steps"
            placeholder="Next steps" class="p-1 h-full w-full" />
    </td>

    <!-- 9. Status — derived on the backend; show as auto -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle text-center text-gray-400 italic">
        auto
    </td>

    <!-- 10. Purpose -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="text" v-model="inventoryItemData.purpose"
            placeholder="full time / spare / ..." class="p-1 h-full w-full" />
    </td>

    <!-- 11. Issued To (holder + issue date if any) -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <div class="flex flex-col gap-1">
            <select :form="'tableForm' + index" v-model="inventoryItemData.issued_to"
                class="p-1 h-full w-full rounded border cursor-pointer text-xs">
                <option :value="null">(in stock)</option>
                <option v-for="option in userOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
            <input v-if="inventoryItemData.issued_to" :form="'tableForm' + index" type="date"
                v-model="inventoryItemData.issued_on" class="p-1 rounded border" />
        </div>
    </td>

    <!-- 12. Brand -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="text"
            :required="!inventoryItemData.model && !inventoryItemData.category"
            v-model="inventoryItemData.brand" placeholder="Brand" class="p-1 h-full w-full" />
    </td>

    <!-- 13. Model -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="text"
            :required="!inventoryItemData.brand && !inventoryItemData.category"
            v-model="inventoryItemData.model" placeholder="Model" class="p-1 h-full w-full" />
    </td>

    <!-- 14. Notes (shown in the Description column slot) -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <textarea :form="'tableForm' + index" v-model="inventoryItemData.notes"
            rows="1" placeholder="Notes" class="p-1 h-full w-full"></textarea>
    </td>

    <!-- 15. Cost -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <input :form="'tableForm' + index" type="number" step="0.01" min="0"
            v-model="inventoryItemData.cost" class="p-1 h-full w-full" />
    </td>

    <!-- 16. Actions -->
    <td class="border-b border-r border-gray-300 px-1 py-1 text-xs align-middle">
        <div class="flex justify-between gap-1">
            <button :form="'tableForm' + index" type="submit"
                class="bg-accent-primary text-white rounded-md py-0.5 px-1 hover:shadow-md" title="Save">
                ✓
            </button>
            <button type="button" @click="close"
                class="bg-dark-secondary text-white rounded-md py-0.5 px-1 hover:shadow-md" title="Cancel">
                ✗
            </button>
        </div>
    </td>
</template>

<script setup>
import { reactive, defineProps, defineEmits, computed } from 'vue';
import { useStore } from 'vuex';
import { createInventoryItem } from '@/services/Inventory';
import { useToast } from 'vue-toast-notification';

const store = useStore();
const $toast = useToast();

const props = defineProps(['index', 'userPermissions', 'userOptions', 'inventoryCategoryOptions']);

const user = computed(() => store.getters['auth/showUser']);

const inventoryItemData = reactive({
    asset_name: null,
    old_device_name: null,
    serial_number: null,
    action1_installed: false,
    action1_specs_captured: false,
    next_steps: null,
    purpose: null,
    issued_to: null,
    issued_on: null,
    brand: null,
    model: null,
    category: null,
    // `notes` is the item-level notes column (the one shown in the grid).
    notes: null,
    cost: 0,
});

const emit = defineEmits(['close-form', 'save-form', 'refetch-results']);

function close() {
    emit('close-form', props.index);
}

async function addInventoryItem() {
    const token = store.state.auth.token;
    const result = await createInventoryItem(inventoryItemData, token);
    if (result.status === 201) {
        $toast.open({
            message: 'Successfully Created New Inventory item.',
            type: 'success',
            duration: 5000,
        });
        emit('refetch-results');
        emit('close-form', props.index);
    } else {
        $toast.open({
            message: 'Error Creating Inventory item.',
            type: 'error',
            duration: 20000,
        });
    }
}
</script>
